import { AuthUserQuery } from 'dashboard-services/api/auth';
import { CommonService } from 'dashboard-services/services';

import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

export default (() => {

  const useUpdateCurrentUserSettings = ({ getQueryBuilder, settings }) => 
    useMutation({
      ...AuthUserQuery.QUERIES.updateCurrentUserSettings(
        getQueryBuilder,
        {
          settings
        }
      )
    })

  const useUpdateUserSettings = ({ getQueryBuilder, userName, settings, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.updateUserSettings(
        getQueryBuilder,
        {
          settings,
          userName
        }
      ),
      ...rest
    })

  const useGetUserSettings = ({ getQueryBuilder, userName, ...rest }) =>
    useQuery({
      ...AuthUserQuery.QUERIES.getUserSettings(
        getQueryBuilder,
        {
          userName
        }
      ),
      ...rest
    })

  const useSearchUsers = ({ query, size = CommonService.getRequestSize(), companyId, roleIds, status, sortBy, sortOrder, getQueryBuilder, shouldRefetch = true, shouldBeLimited, ...rest }) =>
    useInfiniteQuery({
      ...AuthUserQuery.QUERIES.searchUsers(
        getQueryBuilder,
        {
          query,
          size,
          companyId,
          roleIds,
          status,
          sortBy,
          sortOrder
        }
      ),
      initialPageParam: 0,
      getNextPageParam: CommonService.getNextPageParam,
      refetchInterval: () => !shouldRefetch || shouldBeLimited() ? undefined : 10000,
      ...rest
    })

  const useCreateUser = ({ getQueryBuilder, queryClient, userName, companyId, roleIds, settings, registerTemplate, welcomeTemplate, withWelcomeEmail, password, authType, metadata, onSuccess, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.createUser(
        getQueryBuilder,
        {
          userName,
          companyId,
          roleIds,
          settings,
          registerTemplate,
          welcomeTemplate,
          withWelcomeEmail,
          password,
          authType,
          metadata
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: AuthUserQuery.KEYS.SEARCH_USERS() })
        onSuccess?.()
      },
      ...rest
    })


  const useUpdateUser = ({ getQueryBuilder, userName, status, authType, company, sendEmail = false, updateMail = undefined, restoreWhUser= false, queryClient, onSuccess, ...rest }) => 
    useMutation({
      ...AuthUserQuery.QUERIES.updateUser(
        getQueryBuilder,
        {
          userName,
          status,
          authType,
          company,
          sendEmail,
          updateMail,
          restoreWhUser
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: AuthUserQuery.KEYS.SEARCH_USERS() }, { queryKey: AuthUserQuery.KEYS.GET_USER() })
        onSuccess?.()
      },
      ...rest
    })

  const useUpdateUserPassword = ({ getQueryBuilder, userName, password, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.patchUserPassword(
        getQueryBuilder,
        {
          userName,
          password
        }
      ),
      ...rest
    })

  const useResendEmail = ({ getQueryBuilder, userName, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.resendRegistrationEmail(
        getQueryBuilder,
        {
          userName
        }
      ),
      ...rest
    })

  const useGetUser = ({ getQueryBuilder, userName, status, ...rest }) =>
    useQuery({
      ...AuthUserQuery.QUERIES.getUser(
        getQueryBuilder,
        {
          userName,
          status
        }
      ),
      ...rest
    })

  const useGetCurrentUserMetadata = ({ getQueryBuilder, ...rest }) =>
    useQuery({
      ...AuthUserQuery.QUERIES.getCurrentUserMetadata(
        getQueryBuilder
      ),
      ...rest
    })

  const useUpdateUserMetadata = ({ getQueryBuilder, queryClient, userName, metadata, onSuccess, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.updateUserMetadata(
        getQueryBuilder,
        {
          userName,
          metadata
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: AuthUserQuery.KEYS.SEARCH_USERS() }, { queryKey: AuthUserQuery.KEYS.GET_USER() })
        onSuccess?.()
      },
      ...rest
    })

  const useDeleteUser = ({ getQueryBuilder, queryClient, userName, ownershipTargetUserName, onSuccess, ...rest }) =>
    useMutation({
      ...AuthUserQuery.QUERIES.deleteUser(
        getQueryBuilder,
        {
          ownershipTargetUserName,
          userName
        }
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: AuthUserQuery.KEYS.SEARCH_USERS() })
        onSuccess?.()
      },
      ...rest
    })

  const useGetCurrentUserSettings = ({ getQueryBuilder, token, entitlements, ...rest }) =>
    useQuery({
      ...AuthUserQuery.QUERIES.getCurrentUserSettings(
        getQueryBuilder,
        {
          token,
          entitlements
        }
      ),
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...rest
    })

  return {
    useUpdateCurrentUserSettings,
    useSearchUsers,
    useUpdateUser,
    useGetCurrentUserMetadata,
    useCreateUser,
    useResendEmail,
    useGetUser,
    useUpdateUserMetadata,
    useUpdateUserPassword,
    useGetUserSettings,
    useUpdateUserSettings,
    useDeleteUser,
    useGetCurrentUserSettings
  }
})()